import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import PageHeader from "../components/PageHeader";
import StatusPill from "../components/StatusPill";
import NewPatientModal from "../components/NewPatientModal";
import { Search, UserPlus, Filter } from "lucide-react";
import { toast } from "sonner";

const STATUSES = ["Todos", "Nuevo", "Interesado", "Agendado", "Confirmado", "Atendido", "No asistió", "Recuperación", "Inactivo"];

export default function CRM() {
  const [items, setItems] = useState([]);
  const [searchParams] = useSearchParams();
  const [q, setQ] = useState(() => searchParams.get("q") || "");
  const [status, setStatus] = useState("Todos");
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const urlQ = searchParams.get("q") || "";
    setQ(urlQ);
    // eslint-disable-next-line
  }, [searchParams]);

  const load = async () => {
    const params = {};
    if (q) params.q = q;
    if (status !== "Todos") params.status_filter = status;
    const { data } = await api.get("/patients", { params });
    setItems(data.items);
  };

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [q, status]);

  const summary = useMemo(() => {
    const m = { total: items.length, nuevos: 0, agendados: 0, recuperacion: 0 };
    items.forEach((p) => {
      if (p.status === "Nuevo") m.nuevos++;
      if (["Agendado", "Confirmado"].includes(p.status)) m.agendados++;
      if (p.status === "Recuperación") m.recuperacion++;
    });
    return m;
  }, [items]);

  return (
    <div className="p-6 lg:p-8 space-y-5 max-w-7xl mx-auto w-full">
      <PageHeader
        title="Pacientes"
        subtitle="Tu CRM completo: estados y siguiente acción sugerida."
        actions={
          <button onClick={() => setOpen(true)} className="inline-flex items-center gap-2 bg-brand-electric text-white text-sm font-medium px-4 py-2 rounded-lg hover:bg-blue-700 shadow-sm" data-testid="crm-new-patient">
            <UserPlus className="w-4 h-4" /> Nuevo paciente
          </button>
        }
      />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Mini label="Total pacientes" value={summary.total} />
        <Mini label="Leads nuevos" value={summary.nuevos} />
        <Mini label="Agendados / Confirmados" value={summary.agendados} />
        <Mini label="En recuperación" value={summary.recuperacion} />
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl shadow-card overflow-hidden">
        <div className="flex flex-wrap items-center gap-2 p-3 border-b border-slate-200">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar por nombre o teléfono…"
              className="w-full pl-9 pr-3 py-2 text-sm rounded-lg border border-slate-200 bg-slate-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              data-testid="crm-search"
            />
          </div>
          <div className="flex items-center gap-2 text-sm">
            <Filter className="w-4 h-4 text-slate-400" />
            <select value={status} onChange={(e) => setStatus(e.target.value)} className="rounded-lg border border-slate-300 px-2 py-1.5" data-testid="crm-status-filter">
              {STATUSES.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>
        </div>

        {items.length === 0 ? (
          <div className="p-10 text-center text-slate-500 text-sm">Sin pacientes que coincidan con el filtro.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 uppercase text-[11px] font-semibold tracking-wider">
                <tr>
                  <th className="text-left px-4 py-3">Paciente</th>
                  <th className="text-left px-4 py-3">Contacto</th>
                  <th className="text-left px-4 py-3">Fuente</th>
                  <th className="text-left px-4 py-3">Interés</th>
                  <th className="text-left px-4 py-3">Estado</th>
                  <th className="text-left px-4 py-3">Próxima cita</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {items.map((p) => (
                  <tr key={p.id} onClick={() => navigate(`/app/pacientes/${p.id}`)} className="hover:bg-slate-50 transition-colors cursor-pointer" data-testid={`crm-row-${p.id}`}>
                    <td className="px-4 py-3">
                      <Link to={`/app/pacientes/${p.id}`} className="flex items-center gap-3 group">
                        <div className="w-9 h-9 rounded-full bg-blue-100 text-blue-700 grid place-items-center font-semibold text-sm">
                          {p.name[0]}
                        </div>
                        <div>
                          <div className="font-medium text-slate-900 group-hover:text-brand-electric">{p.name}</div>
                          <div className="text-xs text-slate-500">Última: {new Date(p.last_interaction).toLocaleDateString("es-MX")}</div>
                        </div>
                      </Link>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-slate-700">{p.phone}</div>
                      <div className="text-xs text-slate-500">{p.email}</div>
                    </td>
                    <td className="px-4 py-3 text-slate-700">{p.source}</td>
                    <td className="px-4 py-3 text-slate-700">{p.interest}</td>
                    <td className="px-4 py-3"><StatusPill status={p.status} /></td>
                    <td className="px-4 py-3 text-slate-700 text-xs">{p.next_appointment || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <NewPatientModal open={open} onClose={() => setOpen(false)} onCreated={() => { toast.success("Paciente registrado"); load(); }} />
    </div>
  );
}

function Mini({ label, value }) {
  return (
    <div className="bg-white border border-slate-200 rounded-2xl shadow-card p-4">
      <div className="font-display text-2xl font-bold">{value}</div>
      <div className="text-xs text-slate-500 mt-0.5">{label}</div>
    </div>
  );
}
